// Web Vitals reporting utility for Core Web Vitals monitoring
// This can be used to send metrics to analytics services

export interface WebVitalMetric {
  name: string;
  value: number;
  id: string;
  delta: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

// Core Web Vitals thresholds
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  FID: { good: 100, poor: 300 }, // First Input Delay (ms)
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift (score)
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint (ms)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];
  if (!threshold) return 'good';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

export function reportWebVital(metric: WebVitalMetric) {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vital] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
    });
  }

  // Send to Google Analytics if available
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
      custom_map: {
        metric_rating: metric.rating,
      },
    });
  }

  // You can also send to your own analytics endpoint here
  // Example:
  // if (process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT) {
  //   fetch(process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT, {
  //     method: 'POST',
  //     body: JSON.stringify(metric),
  //     headers: { 'Content-Type': 'application/json' },
  //   }).catch(console.error);
  // }
}

// Helper to format metric name
export function formatMetricName(name: string): string {
  const names: { [key: string]: string } = {
    LCP: 'Largest Contentful Paint',
    FID: 'First Input Delay',
    CLS: 'Cumulative Layout Shift',
    FCP: 'First Contentful Paint',
    TTFB: 'Time to First Byte',
  };
  return names[name] || name;
}

// Initialize Web Vitals reporting (call this in _app.tsx)
export function initWebVitals() {
  if (typeof window === 'undefined') return;

  // Only load web-vitals in production
  if (process.env.NODE_ENV !== 'production') return;

  // Dynamically import web-vitals library (optional dependency)
  // @ts-ignore - web-vitals is an optional dependency, may not be installed
  import('web-vitals')
    .then(({ onCLS, onFID, onFCP, onLCP, onTTFB }: any) => {
      onCLS((metric: any) => {
        reportWebVital({
          ...metric,
          rating: getRating('CLS', metric.value),
        });
      });

      onFID((metric: any) => {
        reportWebVital({
          ...metric,
          rating: getRating('FID', metric.value),
        });
      });

      onFCP((metric: any) => {
        reportWebVital({
          ...metric,
          rating: getRating('FCP', metric.value),
        });
      });

      onLCP((metric: any) => {
        reportWebVital({
          ...metric,
          rating: getRating('LCP', metric.value),
        });
      });

      onTTFB((metric: any) => {
        reportWebVital({
          ...metric,
          rating: getRating('TTFB', metric.value),
        });
      });
    })
    .catch(() => {
      // web-vitals library not installed, skip reporting silently
      // This is expected if the package is not installed
    });
}
